<template>
  <div class="form-data-box">
    <div class="row justify-content-md-start">
      <div class="col-md-4">
        <ul class="anchor-menu">
          <li :class="{'active': activeAnchor === 'general'}">
            <a @click="setActiveAnchor('general')">{{ $t('patient.patientInfo') }}</a>
          </li>
          <li :class="{'active': activeAnchor === 'underlying'}">
            <a @click="setActiveAnchor('underlying')">{{ $t('patient.underlyingDisease') }}</a>
          </li>
          <li :class="{'active': activeAnchor === 'other'}">
            <a @click="setActiveAnchor('other')">{{ $t('patient.other') }}</a>
          </li>
          <li :class="{'active': activeAnchor === 'diopter'}">
            <a @click="setActiveAnchor('diopter')">{{ $t('patient.otherInformation') }}</a>
          </li>
        </ul>
      </div>

      <div class="col-md-8 offset-md-4 scrollbar" id="sectionsBox" ref="scrollContainer">
        <section id="general">
          <h3 class="sub-title">{{ $t('patient.patientInfo') }}</h3>
          <div class="form-floating">
            <vee-field name="firstName" v-model="patient.firstName" rules="required" v-slot="{ field, errorMessage }">
              <input v-bind="field" type="text" class="form-control" id="firstName" :placeholder="$t('patient.firstName')" data-test="inputPatientFirstName">
              <span class="vee-error">{{ errorMessage }}</span>
            </vee-field>
            <label for="firstName">{{ $t('patient.firstName') }}*</label>
          </div>
          <div class="form-floating">
            <vee-field name="lastName" v-model="patient.lastName" rules="required" v-slot="{ field, errorMessage }">
              <input v-bind="field" type="text" class="form-control" id="lastName" :placeholder="$t('patient.lastName')" data-test="inputPatientLastName">
              <span class="vee-error">{{ errorMessage }}</span>
            </vee-field>
            <label for="lastName">{{ $t('patient.lastName') }}*</label>
          </div>
          <div class="form-floating">
            <vee-field name="birthday" v-model="patient.birthdayAux" :rules="`date:${$t('dateFormat')}`" v-slot="{ field, errorMessage }">
              <input v-bind="field" type="text" class="form-control"  v-maska :data-maska="patient.birthdayAux ? $t('inputMask.date') : ''" :placeholder="$t('patient.birthday')" data-test="inputPatientBirthday">
              <span class="vee-error">{{ errorMessage }}</span>
            </vee-field>
            <label for="birthday">{{ $t('patient.birthday') }}</label>
          </div>
          <div class="form-floating">
            <input type="text" class="form-control" id="telephone1" v-model="patient.telephone2" :placeholder="$t('patient.telephone1')" data-test="inputPatientPhone1">
            <label for="telephone1">{{ $t('patient.telephone1') }}</label>
          </div>
          <div class="form-floating">
            <vee-field name="email" v-model="patient.email" rules="email" v-slot="{ field, errorMessage }">
              <input v-bind="field" type="text" class="form-control" ref="email" id="email" :placeholder="$t('createClinic.email')" data-test="inputPatientEmail">
              <span class="vee-error">{{ errorMessage }}</span>
            </vee-field>
            <label for="email">{{ $t('createClinic.email') }}</label>
          </div>
          <div class="form-floating">
            <vee-field name="mrn" v-model="patient.mrn" :rules="`${mandatoryDocument === 'mrn' ? 'required|min:1' : ''}`" v-slot="{ field, errorMessage }">
              <input v-bind="field" type="text" class="form-control" id="mrn" :placeholder="$t('mrn')" data-test="inputPatientMrn">
              <span class="vee-error">{{ errorMessage }}</span>
            </vee-field>
            <label for="mrn">
              {{ $t('mrn') }}
              <span v-if="mandatoryDocument === 'mrn'">*</span>
            </label>
          </div>
          <div class="form-floating">
            <vee-field name="document0" v-model="patient.document0" :rules="`${mandatoryDocument === 'document0' ? 'required|min:1' : ''}`" v-slot="{ field, errorMessage }">
              <input v-bind="field" type="text" class="form-control" id="document0" :placeholder="$t('document0')" data-test="inputPatientDocument0">
              <span class="vee-error">{{ errorMessage }}</span>
            </vee-field>
            <label for="document0">
              {{ $t('document0') }}
              <span v-if="mandatoryDocument === 'document0'">*</span>
            </label>
          </div>
          <div class="form-floating">
            <vee-field name="document1" v-model="patient.document1" :rules="`${mandatoryDocument === 'document1' ? 'required|min:1' : ''}`" v-slot="{ field, errorMessage }">
              <input v-bind="field" type="text" class="form-control" id="document1" :placeholder="$t('document1')" data-test="inputPatientDocument1">
              <span class="vee-error">{{ errorMessage }}</span>
            </vee-field>
            <label for="document1">
              {{ $t('document1') }}
              <span v-if="mandatoryDocument === 'document1'">*</span>
            </label>
          </div>
          <div class="form-floating">
            <vee-field name="document2" v-model="patient.document2" :rules="`${mandatoryDocument === 'document2' ? 'required|min:1' : ''}`" v-slot="{ field, errorMessage }">
              <input v-bind="field" type="text" class="form-control" id="document2" v-maska :data-maska="$t('inputMask.document2')" :placeholder="$t('document2')" data-test="inputPatientDocument2">
              <span class="vee-error">{{ errorMessage }}</span>
            </vee-field>
            <label for="document2">
              {{ $t('document2') }}
              <span v-if="mandatoryDocument === 'document2'">*</span>
            </label>
          </div>
          <div class="form-floating">
            <vee-field name="document3" v-model="patient.document3" :rules="`${mandatoryDocument === 'document3' ? 'required|min:1' : ''}`" v-slot="{ field, errorMessage }">
              <input v-bind="field" type="text" class="form-control" id="document3" :placeholder="$t('document3')" data-test="inputPatientDocument3">
              <span class="vee-error">{{ errorMessage }}</span>
            </vee-field>
            <label for="document3">
              {{ $t('document3') }}
              <span v-if="mandatoryDocument === 'document3'">*</span>
            </label>
          </div>
          <div class="form-floating">
            <vee-field name="document4" v-model="patient.document4" :rules="`${mandatoryDocument === 'document4' ? 'required|min:1' : ''}`" v-slot="{ field, errorMessage }">
              <input v-bind="field" type="text" class="form-control" id="document4" :placeholder="$t('document4')" data-test="inputPatientDocument4">
              <span class="vee-error">{{ errorMessage }}</span>
            </vee-field>
            <label for="document4">
              {{ $t('document4') }}
              <span v-if="mandatoryDocument === 'document4'">*</span>
            </label>
          </div>
          <h3 class="sub-title">{{ $t('patient.gender') }}</h3>
          <div class="form-floating gender-box">
            <div class="form-check form-check-inline">
              <input class="form-check-input" type="radio" name="patientGender" id="genderM" value="M" v-model="patient.gender">
              <label class="form-check-label" for="genderM">{{ $t('patient.male') }}</label>
            </div>
            <div class="form-check form-check-inline">
              <input class="form-check-input" type="radio" name="patientGender" id="genderF" value="F" v-model="patient.gender">
              <label class="form-check-label" for="genderF">{{ $t('patient.female') }}</label>
            </div>
            <div class="form-check form-check-inline">
              <input class="form-check-input" type="radio" name="patientGender" id="genderNA" value="NA" v-model="patient.gender">
              <label class="form-check-label" for="genderNA">{{ $t('patient.notInform') }}</label>
            </div>
          </div>
        </section>

        <section id="underlying" v-if="underlyingChange">
          <PatientCreateUnderlyingDiseases :underlying="underlying"/>
        </section>

        <section id="ophthalmic" v-if="ophthalmicChange">
          <PatientCreateOphthalmicDiseases :ophthalmic="ophthalmic"/>
        </section>

        <section id="other" class="localization-pb-33" v-if="patient.anamnesis">
          <h3 class="sub-title">{{ $t('patient.other') }}</h3>
            <div class="form-floating">
              <textarea name="name" class="form-control" id="otherObservation" v-model="patient.anamnesis.otherDisease" :placeholder="$t('patient.other')" data-test="textareaPatientAnamnesisOtherDisease"></textarea>
              <label for="otherObservation">{{ $t('patient.other') }}</label>
            </div>
        </section>

        <section id="diopter">
          <div class="row">
            <!-- TODO componente -> dioptria: map{foo: bar, foo1: bar1} -->
            <div class="col-lg-6">
              <h3 class="sub-title">{{ $t('patient.rightEyeDiopter') }}</h3>
              <div class="form-floating">
                <input type="text" class="form-control" id="right_spherical" v-maska maska-diopter v-model="rightDiopter.spherical" :placeholder="$t('spherical')" data-test="inputPatientRightDiopterSpherical">
                <label for="right_spherical">{{ $t('spherical') }}</label>
              </div>
              <div class="form-floating">
                <input type="text" class="form-control" id="right_cylindrical" v-maska maska-diopter v-model="rightDiopter.cylindrical" :placeholder="$t('cylindrical')" data-test="inputPatientRightDiopterCylindrical">
                <label for="right_cylindrical">{{ $t('cylindrical') }}</label>
              </div>
              <div class="form-floating">
                <input type="text" class="form-control" id="right_axis" v-maska :data-maska="$t('inputMask.axis')" v-model="rightDiopter.axis" :placeholder="$t('axis')" data-test="inputPatientRightDiopterAxis">
                <label for="right_axis">{{ $t('axis') }}</label>
              </div>
            </div>

            <div class="col-lg-6">
              <h3 class="sub-title">{{ $t('patient.leftEyeDiopter') }}</h3>
              <div class="form-floating">
                <input type="text" class="form-control" id="left_spherical" v-maska maska-diopter v-model="leftDiopter.spherical" :placeholder="$t('spherical')" data-test="inputPatientLeftDiopterSpherical">
                <label for="left_spherical">{{ $t('spherical') }}</label>
              </div>
              <div class="form-floating">
                <input type="text" class="form-control" id="left_cylindrical" v-maska maska-diopter v-model="leftDiopter.cylindrical" :placeholder="$t('cylindrical')" data-test="inputPatientLeftDiopterCylindrical">
                <label for="left_cylindrical">{{ $t('cylindrical') }}</label>
              </div>
              <div class="form-floating">
                <input type="text" class="form-control" id="left_axis" v-maska :data-maska="$t('inputMask.axis')" v-model="leftDiopter.axis" :placeholder="$t('axis')" data-test="inputPatientLeftDiopterAxis">
                <label for="left_axis">{{ $t('axis') }}</label>
              </div>
            </div>
          </div>
        </section>

        <section id="localization" class="localization-pb-33">
          <h3 class="sub-title">{{ $t('patient.location') }}</h3>
          <div class="form-floating">
            <input type="text" class="form-control" id="address" v-model="patient.address" :placeholder="$t('patient.address')" data-test="inputPatientAddress">
            <label for="address">{{ $t('patient.address') }}</label>
          </div>
          <div class="form-floating">
            <input type="text" class="form-control" id="addressNumber" v-model="patient.addressNumber" :placeholder="$t('patient.addressNumber')" data-test="inputPatientAddressNumber">
            <label for="addressNumber">{{ $t('patient.addressNumber') }}</label>
          </div>
          <div class="form-floating">
            <input type="text" class="form-control" id="district" v-model="patient.district" :placeholder="$t('patient.district')" data-test="inputPatientDistrict">
            <label for="district">{{ $t('patient.district') }}</label>
          </div>
          <div class="form-floating">
            <input type="text" class="form-control" id="postalCode" v-model="patient.postalCode" :placeholder="$t('patient.postalCode')" data-test="inputPatientPostalCode">
            <label for="postalCode">{{ $t('patient.postalCode') }}</label>
          </div>
          <div class="form-floating">
            <input type="text" class="form-control" id="city" v-model="patient.city" :placeholder="$t('patient.city')" data-test="inputPatientCity">
            <label for="city">{{ $t('patient.city') }}</label>
          </div>
          <div class="form-floating">
            <input type="text" class="form-control" id="state" v-model="patient.state" :placeholder="$t('patient.state')" data-test="inputPatientState">
            <label for="state">{{ $t('patient.state') }}</label>
          </div>
          <div class="form-floating">
            <input type="text" class="form-control" id="country" v-model="patient.country" :placeholder="$t('patient.country')" data-test="inputPatientCountry">
            <label for="country">{{ $t('patient.country') }}</label>
          </div>
        </section>

        
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.col-md-8 {
  max-height:                    calc(100vh - 324px);
}

.form-check {
  margin:                        0;

  &:not(:last-child) {
    margin-bottom:               1rem;
  }

  input {
    width:                       24px;
    height:                      24px;
    margin:                      0;
  }
}

.action-buttons {
  min-height:                  39px;
}

.vee-error {
  width:                       100% !important;
}

.localization-pb-33 {
  padding-bottom:              33px !important;
}

@media (max-width: 991px) {
  .sub-title {
    font-size:                14px;
  }

  .col-md-8 {
    max-height:                calc(100dvh - 296px) !important;
    padding:                   0 15px 0 24px;
  }
}

@media (max-width: 1500px) {
  .col-md-8 {
    max-height:                calc(100vh - 318px);
  }
}

@media screen and (min-width: 992px) and (max-width: 1365px) {
  .col-md-8 {
    max-height:                calc(100vh - 303px);
  }
}
</style>

<script>
  import { useUserStore } from '@/store/user'
  import {MaskInput} from 'maska'
  import PatientCreateOphthalmicDiseases from './PatientCreateOphthalmicDiseases.vue'
  import PatientCreateUnderlyingDiseases from './PatientCreateUnderlyingDiseases.vue'

  export default {
    name: 'PatientFormData',
    props: ['patient',
            'rightDiopter',
            'leftDiopter',
            'underlying',
            'ophthalmic',
            'disabled',
            'underlyingChange',
            'ophthalmicChange',
          ],
    components: {
      PatientCreateOphthalmicDiseases,
      PatientCreateUnderlyingDiseases,
    },
    data: () => ({
      mandatoryDocument: null,
      session: {
        collaborator: {},
        currentClinic: {}
      },
      activeAnchor: 'general'
      // disabled: false,
    }),
    mounted() {
      const userStore = useUserStore()
      this.session = userStore.sessionInfo

      this.mandatoryDocument = this.session.currentClinic.mandatoryDocument

      new MaskInput("[maska-diopter]", {
        mask: (val) => {
          const isNegative = val.startsWith('-'); 
          const absVal = isNegative ? val.slice(1) : val; 

          const lengthMap = {
            2: "inputMask.minDiopter",
            3: "inputMask.shortDiopter",
            4: "inputMask.shortDiopter"
          };

          return `${isNegative ? "-" : ""}${this.$i18n.t(lengthMap[absVal.length] || "inputMask.diopter")}`;
        }
      });

      this.$nextTick(() => {
        const divElement = document.getElementById('sectionsBox')
        divElement.addEventListener('scroll', this.updateActiveAnchor);
        this.updateActiveAnchor(); // Initialize the active section
      })

    },
    beforeUnmount() {
      const divElement = document.getElementById('sectionsBox')
      divElement.removeEventListener('scroll', this.updateActiveAnchor);
    },
    methods: {
      setActiveAnchor(anchor) {
        this.activeAnchor = anchor;
        this.$nextTick(() => {
          const headerHeight = 62;
          const element = document.getElementById(anchor);
          const container = this.$refs.scrollContainer;

          if (element && container) {
            const elementPosition = element.offsetTop;
            const offsetPosition = elementPosition - headerHeight;

            container.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        });
      },
      updateActiveAnchor() {
        const sections = ['general', 'underlying', 'ophthalmic'];
        const divElement = document.getElementById('sectionsBox')
        const divHeight = divElement.offsetHeight
        let currentActive;

        for (const section of sections) {
          // console.log('section', section);
          const element = document.getElementById(section);
          if (!element) continue;

          const rect = element.getBoundingClientRect();
          // console.log('rect.top <= divHeight / 2 && rect.bottom >= divHeight / 2', rect.top <= divHeight / 2 && rect.bottom >= divHeight / 2);
          if (rect.top <= divHeight / 2 && rect.bottom >= divHeight / 2) {
            currentActive = section;
            break;
          }
        }

        if (currentActive && currentActive !== this.activeAnchor) {
          this.activeAnchor = currentActive;
        }
      }
    }
  }
</script>
